import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseISO, differenceInDays } from 'date-fns';
import {
  ArrowRightLeft as DelegationIcon,
  MoveUpRight as OutgoingIcon,
  MoveDownLeft as IncomingIcon,
  FileEdit as DraftsIcon,
  Loader2,
} from 'lucide-react';
import { delegationApi, type DelegationBundle, type DelegationItem } from '../api/services';
import HelpPanel from '../components/HelpPanel';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Delegation bundle (Phase 3 task 3.5). Three panels answer "what's pending
// around me?" in one surface: items I delegated down (are they moving?),
// items I'm owning because someone delegated them to me, and drafts waiting
// for me to promote into a plan. The backend round trip is deliberately
// singular so the tabs just switch views without refetching.
//
// Migrated from MUI to Tailwind + shadcn/ui per MIGRATION_GUIDE.md. Same
// behaviour, ~25% fewer lines.
// ---------------------------------------------------------------------------

// Map of DelegationItem status string → Badge variant. Backend types status
// as a free string for forward compatibility, so we look up by name and fall
// back to 'muted' for anything unrecognised.
const STATUS_VARIANTS: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'muted'> = {
  Backlog: 'muted',
  Planned: 'default',
  InProgress: 'warning',
  Done: 'success',
  Deferred: 'muted',
  Cancelled: 'danger',
};

function DelegationRow({
  item,
  showAssignee,
  showDelegator,
  onClick,
}: {
  item: DelegationItem;
  showAssignee?: boolean;
  showDelegator?: boolean;
  onClick?: () => void;
}) {
  // Age label: "3d overdue" if past target and unfinished, "in 2d" otherwise.
  let age: string | null = null;
  try {
    const diff = differenceInDays(new Date(), parseISO(item.targetDate));
    if (diff > 0 && item.status !== 'Done') age = `${diff}d overdue`;
    else if (diff <= 0) age = `in ${Math.abs(diff)}d`;
  } catch {
    age = null;
  }
  const isOverdue = age?.includes('overdue') ?? false;

  // Left-border colour is the priority cue: Critical → red, High → amber,
  // anything else → muted border. Kept as inline style because the colour
  // is data-driven; the Badge variants already cover the status pill.
  const borderColor =
    item.priority === 'Critical'
      ? 'rgb(239 68 68)' // rose-500
      : item.priority === 'High'
        ? 'rgb(245 158 11)' // amber-500
        : 'hsl(var(--border))';

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
      className={cn(
        'rounded-md px-3 py-2 border-l-2',
        onClick && 'cursor-pointer transition-colors hover:bg-accent',
      )}
      style={{ borderLeftColor: borderColor }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-[180px] flex-1 text-sm font-medium">{item.title}</span>
        <Badge variant={STATUS_VARIANTS[item.status] ?? 'muted'}>{item.status}</Badge>
        {showAssignee && item.assignedToName && (
          <Badge variant="outline">To: {item.assignedToName}</Badge>
        )}
        {showDelegator && item.delegatedByName && (
          <Badge variant="outline">From: {item.delegatedByName}</Badge>
        )}
        {age && (
          <span className={cn(
            'text-xs',
            isOverdue ? 'font-semibold text-rose-600 dark:text-rose-400' : 'text-muted-foreground',
          )}>
            {age}
          </span>
        )}
      </div>
    </div>
  );
}

export default function DelegationPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<DelegationBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'byMe' | 'toMe' | 'drafts'>('byMe');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await delegationApi.get();
        if (!cancelled) setData(response);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Could not load delegation bundle.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return (
      <Alert variant="error" className="mb-4">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  if (!data) return null;

  const openItem = (id: string) => navigate(`/work-items?id=${id}`);

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <DelegationIcon className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Delegation</h1>
          <p className="text-sm text-muted-foreground">
            What you've pushed down, what's been pushed to you, and what's waiting in your drafts tray.
          </p>
        </div>
      </div>

      <HelpPanel title="About Delegation" storageKey="delegation">
        Delegation rolls the three "pending around me" queues into one screen. Delegated by you means items you pushed to another executive. Delegated to you means items a more senior exec handed to you. Drafts are work items you created but haven't committed to a plan yet, often drafted by AI from a transcript or image capture.
      </HelpPanel>

      {/* Summary strip — at-a-glance counts. Left border is the page accent. */}
      <Card className="mb-4 border-l-[3px] border-l-primary">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold">At a glance</span>
            <Badge variant="default" className="gap-1.5">
              <OutgoingIcon className="h-3.5 w-3.5" />
              {data.delegatedByMe.length} delegated by you
            </Badge>
            <Badge variant="default" className="gap-1.5">
              <IncomingIcon className="h-3.5 w-3.5" />
              {data.delegatedToMe.length} delegated to you
            </Badge>
            <Badge variant="default" className="gap-1.5">
              <DraftsIcon className="h-3.5 w-3.5" />
              {data.drafts.length} drafts
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList className="mb-3">
          <TabsTrigger value="byMe" className="gap-1.5">
            <OutgoingIcon className="h-4 w-4" />
            Delegated by me ({data.delegatedByMe.length})
          </TabsTrigger>
          <TabsTrigger value="toMe" className="gap-1.5">
            <IncomingIcon className="h-4 w-4" />
            Delegated to me ({data.delegatedToMe.length})
          </TabsTrigger>
          <TabsTrigger value="drafts" className="gap-1.5">
            <DraftsIcon className="h-4 w-4" />
            Drafts ({data.drafts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="byMe">
          <Card>
            <CardContent className="py-4">
              {data.delegatedByMe.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nothing delegated. When you hand an item to another exec from the Work Items page, it appears here so you can see whether it is moving.
                </p>
              ) : (
                <div className="flex flex-col divide-y divide-border/40">
                  {data.delegatedByMe.map((it) => (
                    <DelegationRow key={it.id} item={it} showAssignee onClick={() => openItem(it.id)} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="toMe">
          <Card>
            <CardContent className="py-4">
              {data.delegatedToMe.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No inbound delegations. Items handed to you by a more senior exec land here and, if still open, stay visible until they're done.
                </p>
              ) : (
                <div className="flex flex-col divide-y divide-border/40">
                  {data.delegatedToMe.map((it) => (
                    <DelegationRow key={it.id} item={it} showDelegator onClick={() => openItem(it.id)} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="drafts">
          <Card>
            <CardContent className="py-4">
              {data.drafts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Drafts tray is empty. Items typed as Draft appear here until you promote them into a plan or delete them. Transcript extractions and image captures are common sources of drafts.
                </p>
              ) : (
                <div className="flex flex-col divide-y divide-border/40">
                  {data.drafts.map((it) => (
                    <DelegationRow key={it.id} item={it} onClick={() => openItem(it.id)} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
